/**
 * O player nunca deve se apresentar ao servidor com um id que não seja o seu.
 *
 * <p><b>Por que existe:</b> o auto-registro do servidor é passivo — a primeira
 * requisição com um `X-Device-Id` desconhecido cria um player. É o que permite
 * provisionar milhares de tablets sem aprovação manual, e é também o que faz
 * qualquer id estranho virar registro permanente na lista da frota. Em 26/09/2026
 * cinco fantasmas apareceram assim no tenant Efflux (`probe`, `probe-android`,
 * `probe-tizen`, `probe-embedded`, `R9XR5083KFT`): nenhum sincronizou, nenhum
 * tinha plataforma, e ninguém sabia de onde vinham.</p>
 *
 * <p>Todo id legítimo é montado pelo próprio player, em `getHardwareId()`, e
 * carrega o prefixo da plataforma: `ANDROID-`, `TIZEN-`, `EMBEDDED-`, `WIN-`…
 * É esse formato que o servidor usa para inferir a plataforma no registro. Um id
 * sem prefixo não identifica nada — entra como player mudo, sem plataforma.</p>
 *
 * <p>Este guarda existe para as duas portas por onde um id assim poderia sair
 * DAQUI: a variável de build `VITE_DEVICE_ID` (que existe para desenvolvimento e
 * é gitignored, então ninguém revisa o valor) e qualquer id gerado na hora como
 * último recurso. O servidor já recusa ids fora do padrão; isto evita que o
 * player chegue a tentar.</p>
 */

/**
 * Prefixos que o servidor reconhece (`inferPlatformFromExternalId`, no
 * `DeviceApiKeyFilter`). Mantidos em sincronia com ele de propósito: um id que o
 * player emite e o servidor recusa é pior que nenhum id — o player fica mudo sem
 * dizer por quê.
 */
const PREFIXOS_VALIDOS = [
  'ANDROID-',
  'TIZEN-',
  'DEV-TIZEN-',
  'EMBEDDED-',
  'WIN-',
  'WEB-',
  'BROWSER-',
  'CHROME-',
  'LINUX-',
];

/** Um id que o servidor saberia de que plataforma é. */
export function ehDeviceIdValido(id: string | null | undefined): boolean {
  if (!id || typeof id !== 'string') return false;
  const upper = id.trim().toUpperCase();
  return PREFIXOS_VALIDOS.some(
    (p) => upper.startsWith(p) && upper.length > p.length,
  );
}

/**
 * Escolhe o id com que o player vai se apresentar.
 *
 * <p>O id do `authStore` é o caminho normal e tem precedência sempre: ele vem do
 * `getHardwareId()` da plataforma. `VITE_DEVICE_ID` é aceito apenas como apoio de
 * desenvolvimento e **só se parecer um id de verdade** — setar
 * `VITE_DEVICE_ID=probe` num build e apontá-lo para produção é exatamente como
 * nascem os fantasmas.</p>
 *
 * @returns o id a usar, ou `null` quando não há nenhum confiável. `null` é
 *          deliberado: melhor o player esperar o id resolver do que inventar um
 *          e criar um registro que ninguém vai saber explicar depois.
 */
export function resolverDeviceId(
  doAuthStore: string | null | undefined,
  daEnv: string | null | undefined,
): string | null {
  if (doAuthStore) return doAuthStore;

  if (daEnv) {
    if (ehDeviceIdValido(daEnv)) return daEnv;
    console.warn(
      `[deviceIdGuard] VITE_DEVICE_ID="${daEnv}" ignorado: não tem prefixo de ` +
        `plataforma (${PREFIXOS_VALIDOS.join(', ')}). Um id assim viraria um player ` +
        'fantasma no servidor — sem plataforma e sem dono.',
    );
  }

  return null;
}
